/**
 * Alerta de stock bajo en la tienda online.
 *
 * Caso de uso: el cliente puede tener la última unidad publicada y ya vendida
 * en el mostrador (la tienda y el POS comparten `stock_levels`, pero entre que
 * el visitante arma el carrito y abona pasa tiempo real). En vez de bloquear la
 * compra, se le avisa antes de pagar y se le ofrece un canal de contacto.
 *
 * La config vive en `store_config.theme_config.lowStockAlert` (jsonb: no hace
 * falta migración). Es opt-in por tienda.
 *
 * Ojo con el pool de stock: `stock_levels` NO discrimina variante, así que
 * todas las líneas del mismo producto comparten un único stock. Por eso la
 * detección agrupa por `product.id` y no por `cartItemId` — si no, el mismo
 * producto aparecería repetido en el cartel una vez por talle.
 */

export const DEFAULT_LOW_STOCK_ALERT = {
    enabled: false,
    threshold: 1,
    message: 'Nos quedan pocas unidades de este producto. Escribinos para confirmar disponibilidad antes de abonar.',
};

/**
 * Normaliza la config guardada. Tolera tiendas viejas (sin la clave), valores
 * cargados como string desde el input numérico y umbrales negativos.
 */
export const getLowStockAlertConfig = (storeConfig) => {
    const raw = storeConfig?.theme_config?.lowStockAlert || {};
    // Ojo: `Number('')` es 0, así que un umbral vacío pasaría por "avisar solo
    // con stock <= 0" en vez de caer al default. "Sin dato" no es cero.
    const rawThreshold = raw.threshold;
    const hasThreshold = rawThreshold !== null && rawThreshold !== undefined && rawThreshold !== '';
    const parsedThreshold = hasThreshold ? Number(rawThreshold) : NaN;
    const threshold = Number.isFinite(parsedThreshold) && parsedThreshold >= 0
        ? Math.floor(parsedThreshold)
        : DEFAULT_LOW_STOCK_ALERT.threshold;
    const message = String(raw.message ?? '').trim();

    return {
        enabled: Boolean(raw.enabled),
        threshold,
        message: message || DEFAULT_LOW_STOCK_ALERT.message,
    };
};

/**
 * Productos del carrito cuyo stock quedó en el umbral o por debajo.
 *
 * Reglas:
 * - Stock sin informar (null/undefined/'' /NaN) NO dispara alerta: "sin dato"
 *   no es "cero", mismo criterio que `getAvailableQty` en storeCartStock.js.
 *   Si no, una tienda que no lleva stock alertaría en todos los productos.
 * - Un producto por entrada, aunque el carrito tenga varias variantes suyas.
 * - Se ordena por stock ascendente: primero lo más crítico.
 *
 * @returns {Array<{id, name, stock}>}
 */
export const getLowStockCartItems = (cartItems, alertConfig) => {
    if (!alertConfig?.enabled) return [];

    const byProduct = new Map();

    (cartItems || []).forEach((item) => {
        if (!item?.id) return;
        const rawStock = item.stock;
        if (rawStock === null || rawStock === undefined || rawStock === '') return;
        const stock = Number(rawStock);
        if (!Number.isFinite(stock)) return;
        if (stock > alertConfig.threshold) return;
        if (byProduct.has(item.id)) return;

        byProduct.set(item.id, {
            id: item.id,
            name: String(item.name || 'Producto').trim() || 'Producto',
            stock,
        });
    });

    return Array.from(byProduct.values()).sort((a, b) => a.stock - b.stock);
};

/**
 * Link de WhatsApp a la tienda con el mensaje prearmado y los productos en
 * duda. Devuelve null si la tienda no cargó WhatsApp (el botón no se muestra y
 * el cartel queda igual, solo informativo).
 */
export const buildLowStockWhatsappLink = (storeConfig, lowStockItems = []) => {
    const phone = String(storeConfig?.whatsapp || '').replace(/[^0-9]/g, '');
    if (!phone) return null;

    const storeName = String(storeConfig?.store_title || '').trim();
    const productList = lowStockItems.map((item) => `- ${item.name}`).join('\n');
    const text = [
        `Hola${storeName ? ` ${storeName}` : ''}, quiero confirmar disponibilidad antes de comprar:`,
        productList,
    ].filter(Boolean).join('\n');

    return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
};
